import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menubar } from 'primereact/menubar';
import { Menu } from 'primereact/menu';
import { Button } from 'primereact/button';

import '../static/styles/NavigationBar.css';

const BASE_ITEMS = [
    { label: 'Home', icon: 'pi pi-home', path: '/home' },
    { label: 'Projects', icon: 'pi pi-hammer', path: '/projects' },
    { label: 'Ask Me Anything', icon: 'pi pi-comments', path: '/ask-me-anything' },
    { label: 'Passport', icon: 'pi pi-compass', path: '/passport' }
];

const PATH_TO_LABEL = BASE_ITEMS.reduce((acc, item) => {
    acc[item.path] = item.label;
    return acc;
}, { '/': 'Home', '/guestbook': 'Passport' });

const MOBILE_BREAKPOINT = 816;

export default function NavigationBar() {
    const navigate = useNavigate();
    const location = useLocation();

    const menuRef = useRef(null);
    const menuButtonRef = useRef(null);

    const [activeItem, setActiveItem] = useState(() => PATH_TO_LABEL[location.pathname] || '');
    const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false));

    useEffect(() => {
        const active = PATH_TO_LABEL[location.pathname]
            || (location.pathname.startsWith('/project') ? 'Projects' : '');
        setActiveItem(active);
        if (menuRef.current?.overlayVisible && menuButtonRef.current) {
            menuRef.current.align({ currentTarget: menuButtonRef.current });
        }
    }, [location.pathname]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleResize = () => {
            setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
        };

        const handleScroll = () => {
            if (menuRef.current?.overlayVisible && menuButtonRef.current) {
                menuRef.current.align({ currentTarget: menuButtonRef.current });
            }
        };

        const scrollOptions = { passive: true };

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll, scrollOptions);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll, scrollOptions);
        };
    }, []);

    const handleMenuItemClick = useCallback((label, path) => {
        setActiveItem(label);
        navigate(path);
        if (menuRef.current?.hide && menuButtonRef.current) {
            menuRef.current.hide({ currentTarget: menuButtonRef.current });
        }
        menuButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [navigate]);

    const customMenuItemTemplate = (item, options) => {
        const isActive = activeItem === item.label;

        return (
            <button
                className={`p-menuitem-link ${isActive ? 'active-menuitem' : ''}`}
                onClick={options.onClick}
                style={{
                    border: 'none',
                    background: isActive ? '#ffffff' : 'transparent',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    color: isActive ? '#000000' : '#ffffff',
                    fontWeight: 'bold',
                    borderRadius: '10rem',
                    transition: 'all 0.3s ease-in-out, transform 0.3s ease-in-out'
                }}
            >
                <span className={options.iconClassName}></span>
                <span className={options.labelClassName}>{item.label}</span>
            </button>
        );
    };

    const menubarItems = BASE_ITEMS.map((item) => ({
        label: item.label,
        icon: item.icon,
        command: () => handleMenuItemClick(item.label, item.path),
        template: customMenuItemTemplate
    }));

    const menuItems = BASE_ITEMS.map((item) => ({
        label: item.label,
        icon: item.icon,
        command: () => handleMenuItemClick(item.label, item.path),
        template: (menuItem, options) => customMenuItemTemplate(menuItem, options)
    }));

    const activeMenuItem = BASE_ITEMS.find((item) => item.label === activeItem) || BASE_ITEMS[0];

    return (
        <div className='container'>
            {isMobile ? (
                <>
                    <Menu model={menuItems} popup ref={menuRef} className='mobile-menu' />
                    <Button
                        className='mobile-menu-button'
                        icon={activeMenuItem.icon}
                        label={activeMenuItem.label}
                        rounded
                        outlined
                        ref={menuButtonRef}
                        onClick={(event) => {
                            menuRef.current?.toggle?.(event);
                            event.currentTarget?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            setTimeout(() => {
                                if (menuRef.current?.overlayVisible && menuButtonRef.current) {
                                    menuRef.current.align({ currentTarget: menuButtonRef.current });
                                }
                            }, 0);
                        }}
                    />
                </>
            ) : (
                <Menubar model={menubarItems} className='menubar' />
            )}
        </div>
    );
}
